/**
 * Company: SPHMedia
 * Description: Category page FeatureCard, PC & Mobile different layout
 */

import type { BFFArticleCard } from "@src/app/types/bff";
import type { Section } from "@src/app/types/Data";
import clsx from "clsx";
import type { ReactElement } from "react";

import Link from "../Link";
import OptimisedImage from "../OptimisedImage/OptimisedImage";
import { BlockTag, SliceTag } from "../SectionTag";
import ArticleTypeIcon from "./ArticleTypeIcon";

type FeatureCardProps = {
  // Define the type for the props required by the component (if any)
  article: BFFArticleCard;
  testId?: number | string;
};

/**
 * FeatureCard
 * Description: Category page FeatureCard, PC & Mobile different layout
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function FeatureCard({
  article,
  testId,
}: FeatureCardProps): ReactElement {
  return (
    <div
      data-testid={`feature-card-${testId}`}
      className={clsx(
        "group relative mx-[12px] my-[16px] shadow-[3px_3px_5px_0_rgba(0,0,0,0.07)] hover:cursor-pointer",
      )}
    >
      <Link to={article.urlPath}>
        <div className="w-full lg:flex">
          <div className="relative lg:w-7/12 xxl:w-1/2 ">
            <OptimisedImage
              rootClassName="aspect-[3/2] lg:group-hover:scale-105"
              src={article.cover}
              suffix={<ArticleTypeIcon itemTypeProps={article.mediaType} />}
              {...{ fetchpriority: "high" }}
            />
            <BlockTag
              className="absolute bottom-0 lg:hidden"
              section={article.section as Exclude<Section, "home">}
            />
          </div>
          <div
            className={`bg-default p-[16px] lg:w-5/12 lg:p-[40px] xxl:w-1/2`}
          >
            <div className="flex flex-col gap-[8px] lg:gap-[12px]">
              <div className="hidden lg:block">
                <SliceTag
                  section={article.section as Exclude<Section, "home">}
                />
              </div>
              <h3 className="line-clamp-3 text-header-3 text-primary lg:text-header-1 lg:group-hover:underline ">
                {article.title}
              </h3>
              <h4 className="line-clamp-1 text-subheader text-tertiary">
                {article.description}
              </h4>
              <main className="line-clamp-3 text-body-2 text-primary lg:line-clamp-4">
                {article.teaser}
              </main>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
